// =============================================================
// Widgets — site-level components that admins can inject into
// page bodies via a marker element.
//
// Marker element (as stored in the page body):
//
//   <span class="widget-marker" data-widget="contact"></span>
//   <span class="widget-marker" data-widget="agenda"></span>
//   ... (any of the 14 kinds from WIDGET_KINDS)
//
// At render time, `expandWidgets(root)` walks the DOM tree of the
// rendered page and replaces each marker element with the
// corresponding widget's HTML. Unknown kinds are removed silently
// so a typo doesn't break the page.
//
// Widget marker also carries `data-modifier` for the visual
// modifier system. main.ts reads both data-widget + data-modifier
// from each marker and applies them on expand.
// =============================================================

import { contactTpl, agendaTpl } from './templates';
import {
  testimonialsTpl, pricingTpl, servicesTpl, teamTpl, galleryTpl,
  faqTpl, statsTpl, openingHoursTpl, quickLoginTpl, registerTpl,
  payTpl, socialTpl,
} from './widget-templates';
import { applyModifier } from './widget-modifiers';
import type { WidgetKind, WidgetModifier } from './widget-config';

export type { WidgetKind, WidgetModifier } from './widget-config';

export const ALL_WIDGET_KINDS: WidgetKind[] = [
  'contact', 'agenda', 'testimonials', 'pricing', 'services', 'team',
  'gallery', 'faq', 'stats', 'opening-hours', 'quick-login', 'register',
  'pay', 'social',
];

/** Render a widget's HTML for the current locale. */
export function renderWidget(kind: WidgetKind, config: Record<string, unknown> = {}): string {
  switch (kind) {
    case 'contact':         return contactTpl();
    case 'agenda':          return agendaTpl();
    case 'testimonials':    return testimonialsTpl(config);
    case 'pricing':         return pricingTpl(config);
    case 'services':        return servicesTpl(config);
    case 'team':            return teamTpl(config);
    case 'gallery':         return galleryTpl(config);
    case 'faq':             return faqTpl(config);
    case 'stats':           return statsTpl(config);
    case 'opening-hours':   return openingHoursTpl(config);
    case 'quick-login':     return quickLoginTpl(config);
    case 'register':        return registerTpl(config);
    case 'pay':             return payTpl(config);
    case 'social':          return socialTpl(config);
    default:                return '';
  }
}

/** Human-friendly label for the admin editor's "Insert widget" menu. */
export function widgetLabel(kind: WidgetKind): string {
  switch (kind) {
    case 'contact':         return 'Contact form';
    case 'agenda':          return 'Agenda';
    case 'testimonials':    return 'Testimonials';
    case 'pricing':         return 'Pricing';
    case 'services':        return 'Services';
    case 'team':            return 'Team';
    case 'gallery':         return 'Gallery';
    case 'faq':             return 'FAQ';
    case 'stats':           return 'Stats';
    case 'opening-hours':   return 'Opening hours';
    case 'quick-login':     return 'Quick login';
    case 'register':        return 'Registration';
    case 'pay':             return 'Pay';
    case 'social':          return 'Social links';
  }
}

const WIDGET_ORDER: WidgetKind[] = ALL_WIDGET_KINDS;

export { WIDGET_ORDER };

/** Marker emitted by the page editor when a widget is inserted.
 * The marker carries the modifier so render-time wiring doesn't need
 * to know which modifier each widget has. */
export function widgetMarker(kind: WidgetKind, modifier: WidgetModifier = 'none', config: Record<string, unknown> = {}): string {
  // Inline the config as a JSON-encoded data attribute so each marker
  // is self-contained — editors can rearrange / clone markers without
  // losing per-instance settings.
  const safeConfig = encodeURIComponent(JSON.stringify(config || {}));
  return `<span class="widget-marker" data-widget="${kind}" data-modifier="${modifier}" data-config="${safeConfig}"></span>`;
}

/** Walk the DOM, replace every widget marker with rendered HTML. */
export function expandWidgets(root: HTMLElement): void {
  const markers = Array.from(root.querySelectorAll<HTMLElement>('.widget-marker'));
  for (const marker of markers) {
    const kind = marker.dataset.widget as WidgetKind | undefined;
    if (!kind) { marker.remove(); continue; }
    const modifier = (marker.dataset.modifier as WidgetModifier | undefined) || 'none';
    let config: Record<string, unknown> = {};
    try {
      if (marker.dataset.config) config = JSON.parse(decodeURIComponent(marker.dataset.config));
    } catch { /* malformed config — render with defaults */ }
    const html = renderWidget(kind, config);
    if (!html) { marker.remove(); continue; }
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const widget = tmp.firstElementChild as HTMLElement | null;
    if (!widget) { marker.remove(); continue; }
    marker.replaceWith(widget);
    // Apply modifier class + observer to the widget root.
    applyModifier(widget, modifier);
  }
  // Hydrate any data-bound widgets (e.g. opening-hours reads from
  // window.__SLOTH_BUSINESS__).
  hydrateDataBindings(root);
}

/** Find [data-bind] widgets and fill them with tenant data. */
function hydrateDataBindings(root: ParentNode): void {
  const business = (window as unknown as { __SLOTH_BUSINESS__?: any }).__SLOTH_BUSINESS__;
  if (!business) return;
  const hoursWidget = root.querySelector<HTMLElement>('[data-bind="opening-hours"]');
  if (hoursWidget) {
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const labels: Record<string, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
    const rows = days.map(d => {
      const v = business.hours?.[d] || 'closed';
      return `<div class="hours-row"><span class="hours-day">${labels[d]}</span><span class="hours-time">${escapeHtml(v)}</span></div>`;
    }).join('');
    hoursWidget.innerHTML = `<div class="hours-table">${rows}</div>`;
  }
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}
